import * as React from 'react';

import { useLockFn } from 'ahooks';
import { Flex, Form, notification } from 'antd';
import { useNavigate } from 'react-router-dom';

import { PrimitiveForm } from 'components';
import { usePaymentGatewayValue } from 'context';
import { updatePaymentGateway } from 'services';
import { fixedParseFloat, PATHNAME } from 'utils';

import { PaymentGatewayFormAction } from './action';
import { PaymentGatewayFormContent } from './content';

const {
  LOCAL: {
    PAYMENT_GATEWAYS: { BASE_URL: PAYMENT_GATEWAYS_BASE_URL },
  },
} = PATHNAME;

type FieldType = Pick<
  ReturnType<typeof usePaymentGatewayValue>,
  'title' | 'description' | 'enable'
>;

const PaymentGatewayForm = () => {
  const [form] = Form.useForm<FieldType>();
  const {
    id = '',
    iconId,
    defaultTitle,
    mode,
    discountType,
    discountAmount,
  } = usePaymentGatewayValue();
  const navigate = useNavigate();

  const formattedDiscountAmount = React.useMemo<number | undefined>(() => {
    if (typeof discountAmount !== 'number') return undefined;

    const fixedAmount = fixedParseFloat(discountAmount, 3);

    if (discountType !== 'Percentage') return fixedAmount;

    return Math.min(Math.max(0, fixedAmount), 100);
  }, [discountType, discountAmount]);

  const onFinish = useLockFn(
    async ({ title = '', description = '', enable = false }: FieldType) => {
      const isUpdatedMode = mode === 'Update';

      const params: Parameters<typeof updatePaymentGateway>[0] = {
        id,
      };
      const body: Parameters<typeof updatePaymentGateway>[1] = {
        title,
        description,
        iconId,
        enable,
        discountType,
        discountAmount: discountType ? formattedDiscountAmount ?? 0 : 0,
      };

      (isUpdatedMode
        ? updatePaymentGateway(params, body)
        : updatePaymentGateway(params, body)
      )
        .then(() => {
          notification.success({
            message: `درگاه پرداخت ${isUpdatedMode ? defaultTitle : title} با موفقیت ${isUpdatedMode ? 'ویرایش' : 'ساخته'} شد.`,
          });
          navigate(PAYMENT_GATEWAYS_BASE_URL);
        })
        .catch(message => {
          notification.error({ message });
        });
    },
  );

  return (
    <PrimitiveForm
      form={form}
      fields={[
        {
          name: 'discountAmount',
          value: formattedDiscountAmount,
        },
      ]}
      onFinish={onFinish}
    >
      <Flex
        className='*:rounded *:bg-white max-xl:flex-col max-xl:last:*:sticky max-xl:last:*:inset-x-0 max-xl:last:*:bottom-0 max-xl:last:*:z-10 max-xl:last:*:shadow-inner xl:items-start xl:first:*:basis-[70%] xl:last:*:basis-[30%]'
        gap='large'
      >
        <PaymentGatewayFormContent />
        <PaymentGatewayFormAction />
      </Flex>
    </PrimitiveForm>
  );
};

export { PaymentGatewayForm };
